import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { desktop, type DesktopContext } from '@/lib/desktop';
import { Card, ErrorNote, Spinner } from './ui';

/**
 * Desktop-only controls. Rendered by the tracker page; returns null in the
 * browser so the same bundle serves both.
 */
export function DesktopPanel() {
  const bridge = desktop();
  const { settings } = useAuth();
  const [ctx, setCtx] = useState<DesktopContext | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    if (!bridge) return;
    setCtx(await bridge.getContext());
  }, [bridge]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (!bridge) return;
    return bridge.onPermissionRequired(() => {
      setError(new Error('Screen recording permission is required before screenshots can be captured.'));
      void refresh();
    });
  }, [bridge, refresh]);

  if (!bridge) return null;

  const needsPermission = ctx && ctx.platform === 'darwin' && ctx.screenPermission !== 'granted';

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-4 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Desktop app</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Version {ctx?.appVersion ?? '…'} · connected to {ctx?.apiBaseUrl ?? bridge.apiBaseUrl}
          </p>
        </div>
        {ctx && ctx.pendingUploads > 0 && (
          <span className="badge bg-amber-100 text-amber-800">{ctx.pendingUploads} upload{ctx.pendingUploads === 1 ? '' : 's'} queued</span>
        )}
      </div>

      <ErrorNote error={error} />

      {needsPermission && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <p className="font-medium">Screen recording permission needed</p>
          <p className="mt-0.5 text-xs">
            macOS will not let TickTock capture your screen until you allow it in System Settings → Privacy &amp; Security → Screen
            Recording. TickTock must be restarted afterwards.
          </p>
          <button className="btn-secondary mt-2 py-1 text-xs" disabled={busy} onClick={() => void run(async () => { await bridge.requestScreenPermission(); })}>
            Open System Settings
          </button>
        </div>
      )}

      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-slate-800">Input counting: <span className="font-medium">{ctx?.activityMode ?? '…'}</span></p>
            <p className="text-xs text-slate-500">
              {ctx?.activityMode === 'precise'
                ? 'Real keystroke and mouse event counts from the native input hook.'
                : 'Activity level from the idle timer and cursor polling. Precise mode counts real events; on macOS it needs Accessibility and Input Monitoring.'}
            </p>
          </div>
          {ctx?.activityMode !== 'precise' && ctx?.platform === 'darwin' && (
            <button className="btn-secondary shrink-0 py-1 text-xs" disabled={busy} onClick={() => void run(async () => { await bridge.enablePreciseActivity(); })}>
              Enable precise
            </button>
          )}
        </div>
        <Row
          label="Notify me when a screenshot is taken"
          hint={
            ctx?.notifyOnCaptureLocked
              ? 'Switched off for everyone by an administrator. Captures continue either way.'
              : 'Recommended. Turning this off does not stop the captures.'
          }
          checked={ctx?.notifyOnCapture ?? true}
          disabled={busy || !!ctx?.notifyOnCaptureLocked}
          onChange={(v) => void run(() => bridge.setNotifyOnCapture(v))}
        />
        <Row
          label="Launch TickTock at login"
          hint="Starts minimised in the menu bar / system tray."
          checked={ctx?.launchAtLogin ?? false}
          disabled={busy}
          onChange={(v) => void run(() => bridge.setLaunchAtLogin(v))}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
        <button
          className="btn-secondary py-1.5 text-xs"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const res = await bridge.captureNow();
              setMessage(
                res.reason === 'not-tracking'
                  ? 'Start the timer first — TickTock never captures while you are clocked out.'
                  : res.skippedReason === 'no-permission'
                    ? 'Blocked: screen recording permission has not been granted.'
                    : `Captured ${res.captured} screen${res.captured === 1 ? '' : 's'}.`,
              );
            })
          }
        >
          {busy && <Spinner className="h-3 w-3" />} Capture now
        </button>
        {message && <span className="text-xs text-slate-600">{message}</span>}
        <span className="ml-auto text-xs text-slate-400">
          Captures every {settings?.screenshotIntervalMinutes ?? 10} min while the timer runs · idle after{' '}
          {settings?.idleTimeoutMinutes ?? 10} min
        </span>
      </div>
    </Card>
  );
}

function Row({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm text-slate-800">{label}</p>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-50 ${checked ? 'bg-brand-600' : 'bg-slate-300'}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? 'left-[1.375rem]' : 'left-0.5'}`} />
      </button>
    </div>
  );
}
